import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../store.jsx";
import { useRole } from "../../hooks/useRole";
import { useProjects } from "../../hooks/useProjects";
import { getProjectsMeta } from "../../lib/api";
import { Icon, EmptyState, Pagination } from "../../components/Common.jsx";
import PageBoundary from "../../components/PageBoundary.jsx";
import ProjectCard from "../../components/projects/ProjectCard.jsx";
import ProjectFormModal from "../../components/projects/ProjectFormModal.jsx";

function ProjectsInner() {
  const { goPage, openModal } = useApp();
  const role = useRole();
  const canManage = role === "owner" || role === "manager";

  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [meta, setMeta] = useState({ statuses: [] });

  useEffect(() => {
    getProjectsMeta()
      .then(setMeta)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters = useMemo(() => {
    const f = { page, limit: pageSize, sort: "createdAt", dir: "desc" };
    if (status) f.status = status;
    if (search) f.search = search;
    return f;
  }, [status, search, page, pageSize]);

  const { projects, total, loading, error, refresh } = useProjects(filters);
  if (error) throw error; // bubble to PageBoundary

  const openCreate = () => openModal(<ProjectFormModal onSaved={refresh} />, "lg");
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize;
  const to = Math.min(total, from + projects.length);

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-sub">
            {canManage
              ? `${total} project${total === 1 ? "" : "s"}`
              : "Projects you're a member of"}
          </p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={16} /> New Project
          </button>
        )}
      </div>

      <div className="table-toolbar">
        <div className="toolbar-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Search projects…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search projects"
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className={`filter-chip ${status === "" ? "active" : ""}`}
            onClick={() => {
              setStatus("");
              setPage(1);
            }}
          >
            All
          </button>
          {meta.statuses.map((s) => (
            <button
              key={s.value}
              className={`filter-chip ${status === s.value ? "active" : ""}`}
              onClick={() => {
                setStatus(s.value);
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
          Loading projects…
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          icon="folder"
          desc={
            canManage
              ? "Create your first project to track work across daily reports."
              : "You haven't been added to any projects yet."
          }
          cta={canManage ? "New Project" : undefined}
          onCta={canManage ? openCreate : undefined}
        />
      ) : (
        <>
          <div className="project-grid">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                meta={meta}
                onOpen={() => goPage("projectDetail", { projectId: p.id })}
              />
            ))}
          </div>
          {total > pageSize && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              from={from}
              to={to}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

export default function Projects() {
  return (
    <PageBoundary>
      <ProjectsInner />
    </PageBoundary>
  );
}
